import {useTranslation} from "react-i18next";
import {useEffect, useRef} from "react";
import {ForkOutlined} from "@ant-design/icons";
import {Button, Form, Input, InputNumber, Select} from "antd";
import ApiService from "../../core/service/api-service";
import ModalitySelectBox from "../modality/ModalitySelectBox";
import FirmSelectBox from "../firm/FirmSelectBox";
import {useHistory} from "react-router-dom";
import {Pages} from "../../AppRouter";
import StudyTypeEnum from "../../enums/StudyTypeEnum";

const timezoneOptions = [
    { label: 'UTC-12:00', value: -720 },
    { label: 'UTC-11:00', value: -660 },
    { label: 'UTC-10:00', value: -600 },
    { label: 'UTC-09:00', value: -540 },
    { label: 'UTC-08:00', value: -480 },
    { label: 'UTC-07:00', value: -420 },
    { label: 'UTC-06:00', value: -360 },
    { label: 'UTC-05:00', value: -300 },
    { label: 'UTC-04:00', value: -240 },
    { label: 'UTC-03:00', value: -180 },
    { label: 'UTC-02:00', value: -120 },
    { label: 'UTC-01:00', value: -60 },
    { label: 'UTC+00:00', value: 0 },
    { label: 'UTC+01:00', value: 60 },
    { label: 'UTC+02:00', value: 120 },
    { label: 'UTC+03:00 (Türkiye)', value: 180 },
    { label: 'UTC+04:00', value: 240 },
    { label: 'UTC+05:00', value: 300 },
    { label: 'UTC+05:30', value: 330 },
    { label: 'UTC+06:00', value: 360 },
    { label: 'UTC+07:00', value: 420 },
    { label: 'UTC+08:00', value: 480 },
    { label: 'UTC+09:00', value: 540 },
    { label: 'UTC+10:00', value: 600 },
    { label: 'UTC+11:00', value: 660 },
    { label: 'UTC+12:00', value: 720 },
];

export default function HospitalCreateEdit({hospitalId})
{
    const {t} = useTranslation();
    const history = useHistory();
    const formRef = useRef();

    const getHospital = async () => {
        if(hospitalId)
        {
            const response = await ApiService.Hospital.Get(hospitalId);
            if(response.success)
            {
                let mins = {}
                Object.keys(StudyTypeEnum).forEach((key, idx) => {
                    const data = response.data.studyTypesMin[StudyTypeEnum[key]-1]
                    let min = '1440'
                    let timeout = '1440'

                    if(data) {
                        min = data.split(':')[0]
                        timeout = data.split(':')[1]
                    }
                    mins[`number_${key}_min`] = min
                    mins[`number_${key}_timeout`] = timeout
                })

                formRef.current.setFieldsValue({
                    firmId: response.data.firm.id,
                    name: response.data.name,
                    modalityIdList: response.data.modalityIdList,
                    officialName: response.data.officialName,
                    timezoneOffset: response.data.timezoneOffset ?? 180,
                    studyLock: response.data.studyLock,
                    modalityMapping: response.data.modalityMapping,
                    ...mins
                })
            }
        }
    }

    const save = async (form) => {
        let studyTypesMin = [];
        Object.keys(StudyTypeEnum).forEach(key => {
            const min = form[`number_${key}_min`]
            const timeout = form[`number_${key}_timeout`]
            studyTypesMin.push(`${min}:${timeout}`)
        })
        form.studyTypesMin = studyTypesMin

        if(hospitalId)
        {
            const response = await ApiService.Hospital.Edit(hospitalId, form)
        }
        else
        {
            const response = await ApiService.Hospital.Create(form);
            if(response.success)
                history.push(Pages.Hospital.Edit(response.data))
        }
    }

    useEffect(() => {
        getHospital()
    }, [])

    return (
        <>
            <Form
                layout={'vertical'}
                ref={formRef}
                onFinish={save}
            >

                <Form.Item
                    label={t('Firm')}
                    name={'firmId'}
                    rules={[
                        {required: true}
                    ]}
                >
                    <FirmSelectBox />
                </Form.Item>

                <Form.Item
                    name={'name'}
                    label={t('Name')}
                    rules={[
                        {required: true}
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name={'officialName'}
                    label={t('Official Name')}
                    rules={[
                        {required: true}
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name={'timezoneOffset'}
                    label={t('Timezone')}
                    initialValue={180}
                    rules={[
                        {required: true}
                    ]}
                >
                    <Select 
                        options={timezoneOptions}
                        showSearch
                        optionFilterProp="label"
                        style={{width: '100%'}}
                    />
                </Form.Item>

                <Form.Item
                    name={'studyLock'}
                    label={t('Study Lock')}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name={'modalityMapping'}
                    label={t('Modality Mapping')}
                    tooltip={t('Maps source modality to target modality')}
                >
                    <Input placeholder="DX:CR,US:MR" />
                </Form.Item>

                {
                    Object.keys(StudyTypeEnum).map(key => {

                        return (
                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                <Form.Item
                                    name={`number_${key}_min`}
                                    label={`${t(key)} (Min/Timeout)`}
                                    rules={[
                                        {required: true}
                                    ]}
                                >
                                    <InputNumber
                                        style={{width: 225}} />
                                </Form.Item>
                                <Form.Item
                                    name={`number_${key}_timeout`}
                                    label={` `}
                                    required={false}
                                    rules={[
                                        {required: true}
                                    ]}
                                >
                                    <InputNumber
                                        style={{width: 225}} />
                                </Form.Item>
                            </div>
                        )
                    })
                }

                <Form.Item
                    label={t('Modalities')}
                    name={'modalityIdList'}
                    rules={[{required: true}]}
                >
                    <ModalitySelectBox />
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit">
                        {t('Save')}
                    </Button>
                </Form.Item>
            </Form>
        </>
    )
}
